import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  Observable,
  TimeoutError,
  catchError,
  delay,
  map,
  of,
  switchMap,
  tap,
  throwError,
  timeout,
} from 'rxjs';

import { STORAGE_KEYS } from '../../../core/constants/storage-keys';
import { ApiService } from '../../../core/services/api.service';
import { environment } from '../../../../environments/environment';
import { MOCK_AUTH_ACCOUNT } from '../data/auth.mock';
import {
  AuthSession,
  LoginApiResponse,
  LoginRequest,
  LoginResponse,
} from '../models/auth.models';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiService = inject(ApiService);
  private readonly router = inject(Router);
  private readonly sessionState = signal<AuthSession | null>(this.readStoredSession());

  readonly session = this.sessionState.asReadonly();
  readonly currentUser = computed(() => this.session()?.user ?? null);
  readonly isAuthenticated = computed(() => Boolean(this.session()?.accessToken));

  get accessToken(): string | null {
    return this.session()?.accessToken ?? null;
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    if (!environment.useMockAuthApi) {
      return this.apiService
        .post<LoginApiResponse>('/auth/login-user', {
          data: credentials,
        })
        .pipe(
          timeout(15000),
          map((response) => this.mapApiLoginResponse(response)),
          tap((response) => this.persistSession(response)),
          catchError((error: unknown) => {
            if (error instanceof TimeoutError) {
              return throwError(() => new Error('El servidor tardó demasiado en responder.'));
            }

            const httpError = error as HttpErrorResponse;
            const apiMessage =
              (httpError.error as { message?: string } | undefined)?.message ??
              'No se pudo iniciar sesión con el servicio.';

            return throwError(() => new Error(apiMessage));
          }),
        );
    }

    const normalizedEmail = credentials.email.trim().toLowerCase();

    return of(MOCK_AUTH_ACCOUNT).pipe(
      delay(800),
      switchMap((account) => {
        const matchesUser = account.user.email.toLowerCase() === normalizedEmail;

        if (!matchesUser || account.password !== credentials.password) {
          return throwError(() => new Error('Las credenciales no son válidas.'));
        }

        const response: LoginResponse = {
          accessToken: account.accessToken,
          user: account.user,
          expiresAt: account.expiresAt,
        };

        this.persistSession(response);
        return of(response);
      }),
    );
  }

  changeOwnPassword(newPassword: string): Observable<void> {
    const userId = this.currentUser()?.id;

    if (!userId) {
      return throwError(() => new Error('No se encontró el usuario autenticado.'));
    }

    return this.apiService
      .postText('/user/change-password', {
        data: {
          UsuId: userId,
          UsuPassword: newPassword,
        },
      })
      .pipe(
        timeout(15000),
        map(() => void 0),
        tap(() => this.clearMustChangePassword()),
        catchError((error: unknown) => {
          if (error instanceof TimeoutError) {
            return throwError(() => new Error('El servidor tardó demasiado en responder.'));
          }

          const httpError = error as HttpErrorResponse;
          const errorBody = httpError.error as
            | string
            | { message?: string; error?: string; title?: string }
            | null
            | undefined;

          const message =
            (typeof errorBody === 'string' && errorBody.trim()) ||
            (typeof errorBody === 'object' && errorBody?.message) ||
            (typeof errorBody === 'object' && errorBody?.error) ||
            (typeof errorBody === 'object' && errorBody?.title) ||
            'No se pudo cambiar la contraseña.';

          return throwError(() => new Error(message));
        }),
      );
  }

  logout(redirectToLogin = true): void {
    this.clearSessionStorage();
    this.sessionState.set(null);

    if (redirectToLogin) {
      void this.router.navigate(['/login']);
    }
  }

  private persistSession(response: LoginResponse): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEYS.authSession, JSON.stringify(response));
    }

    this.sessionState.set(response);
  }

  private readStoredSession(): AuthSession | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }

    const storedSession = sessionStorage.getItem(STORAGE_KEYS.authSession);

    if (!storedSession) {
      return null;
    }

    try {
      const parsedSession = JSON.parse(storedSession) as AuthSession;

      if (new Date(parsedSession.expiresAt).getTime() <= Date.now()) {
        this.clearSessionStorage();
        return null;
      }

      return parsedSession;
    } catch {
      this.clearSessionStorage();
      return null;
    }
  }

  private clearMustChangePassword(): void {
    const session = this.sessionState();

    if (!session) {
      return;
    }

    this.persistSession({
      ...session,
      user: { ...session.user, mustChangePassword: false },
    });
  }

  private clearSessionStorage(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEYS.authSession);
      sessionStorage.removeItem(STORAGE_KEYS.selectedCompanyId);
    }
  }

  private mapApiLoginResponse(response: LoginApiResponse): LoginResponse {
    if (response.statusCode !== 200 || !response.data?.usuario || !response.data.token) {
      throw new Error(response.message || 'La respuesta del servicio de autenticación no es válida.');
    }

    const apiUser = response.data.usuario;
    const expiresAt = this.getTokenExpirationDate(response.data.token) ?? response.date;

    return {
      accessToken: response.data.token,
      expiresAt,
      user: {
        id: apiUser.usuId,
        companyId: this.readNumber(apiUser.empId, apiUser.EmpId, apiUser.usuEmpId, apiUser.UsuEmpId),
        companyName: this.readString(apiUser.empNombre, apiUser.EmpNombre) || undefined,
        firstName: apiUser.usuNombre,
        lastName: apiUser.usuApellido,
        fullName: `${apiUser.usuNombre} ${apiUser.usuApellido}`.trim(),
        email: apiUser.usuEmail,
        roleId: apiUser.rolId,
        roleName:
          this.readString(
            apiUser.rolNombre,
            apiUser.RolNombre,
            apiUser.roleName,
            apiUser.RoleName,
          ) || undefined,
        status: apiUser.usuEstado,
        createdAt: apiUser.usuFcreacion,
        mustChangePassword: Boolean(apiUser.usuChangePassword ?? apiUser.UsuChangePassword),
      },
    };
  }

  private getTokenExpirationDate(token: string): string | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number };

      if (!payload.exp) {
        return null;
      }

      return new Date(payload.exp * 1000).toISOString();
    } catch {
      return null;
    }
  }

  private readNumber(...values: Array<number | null | undefined>): number | null {
    return values.find((value) => typeof value === 'number' && value > 0) ?? null;
  }

  private readString(...values: Array<string | null | undefined>): string {
    return values.find((value) => typeof value === 'string')?.trim() ?? '';
  }
}
